import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { DataRole } from '@mode-switch/express';
import { Model } from '@mode-switch/model';
import { RecordsPlan, RecordsQuery } from '@mode-switch/query';
import { Encoding } from '@mode-vegas/vegas';
import { ChartActions, TempActions, fromChartSelectors } from '@mode/charts/domain';
import {
  ChartFormatHelpers,
  EncodingHelpers,
  Flamingo,
  QueryBuilder,
  SwitchSchemaModel,
} from '@mode/charts/util-vegas';
import { LegacyReportFilterControl } from '@mode/filters/util';
import { ofType } from '@mode/legacy/util-domain';
import { reportEditActions } from '@mode/report-editor/domain';
import { ReportViewerFacade } from '@mode/report-viewer/domain';
import { ViewDataModalActions, ViewDataModalSelectors } from '@mode/reports/domain';
import { FlamingoResponse } from '@mode/shared/contract-common';
import { FlamingoClientService } from '@mode/shared/data-access-flamingo';
import {
  DEFAULT_FLAMINGO_PAGE_SIZE,
  FLAMINGO_RESERVED_REFS,
  dimensions,
  makeQueryPlanMetadataId,
  makeQueryWithCalcsPlanMetadataId,
} from '@mode/shared/util-js';
import {
  DataViewFacade,
  FlamingoActions,
  PlanMetadataFacade,
  SwitchAttrsActions,
  SwitchViewActions,
} from '@mode/switch-views/contract';
import { Observable, SchedulerLike, combineLatest } from 'rxjs';
import {
  debounceTime,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Actions } from '~/actions';
import { ActiveExplorationSelectors } from '~/selectors/active-exploration.selectors';
import { ChartDesignerActionType } from '../actions/chart-designer/designer.actions';
import { QueryActionType } from '../actions/query.actions';
import { SqlResultActions } from '../actions/sql-results.actions';
import { FLAMINGO_UNSUPPORTED_CHARTS } from '../common/constants';
import { addReportFiltersToChartEncoding } from '../lib/chart-encoding-helpers';
import { isChart } from '../lib/chart-helpers';
import { reportFilterControlsSelector, transientSelectedQueryTokenSelector } from '../reducers/selectors';
import { watchMap } from '../rxjs-operators/watchMap';
import { ChartDesignerSelectors } from '../selectors/designer.selectors';
import { ExplorerSelectors } from '../selectors/explorer.selectors';
import { SqlResultsSelectors } from '../selectors/sql-results.selectors';
import { AppStore } from '../types/main.types';
import { FlamingoFilterService } from './flamingo-filter-service';

const PAGING_DEBOUNCE_TIME = 150;

export class FlamingoError extends Error {
  constructor(public response: FlamingoResponse.FlamingoErrorResponse) {
    super(response.type);
    Object.setPrototypeOf(this, FlamingoError.prototype);
  }
}

const adapter = new FlamingoAdapter();

export class FlamingoService {
  static $inject = [
    '$window',
    '$rootScope',
    'flamingoHost',
    'FlamingoClient',
    'RxStoreService',
    'actions$',
    'FlamingoFilterService',
    'ReportViewerFacade',
    'DataViewFacade',
    'PlanMetadataFacade',
  ];

  constructor(
    private $window: angular.IWindowService,
    private $root: angular.IRootScopeService,
    private flamingoHost: string,
    private flamingoClient: FlamingoClientService,

    private rxStore: AppStore,
    private actions$: Observable<Actions>,
    _: FlamingoFilterService,
    private reportViewerFacade: ReportViewerFacade,
    private dataViewFacade: DataViewFacade,
    private planMetadataFacade: PlanMetadataFacade
  ) {
    /************************************
     *
     * EXPLORATION FLAMINGO EFFECTS - START
     * TODO: organize this file to break up the effects
     *
     ***********************************/
    // Build exploration$ observable
    // Currently we are merging 2 observables, one represents what gets built in the prod flow, and the other
    // represents what gets built in the GF flow. Realistically, only one or the other will ever be triggered
    // in a single request.
    const exploration$ = actions$.pipe(
      ofType(TempActions.loadExploration.type),
      switchMap(() =>
        rxStore.selectNoEmpty(fromChartSelectors.createChartTypeEncodingSelector(FLAMINGO_RESERVED_REFS.Exploration))
      ),
      filter(({ encoding, chartType }) => EncodingHelpers.isChartEncodingComplete(encoding, chartType)),
      switchMap(({ queryToken, chartType, chartToken, encoding }) =>
        rxStore
          .selectNoEmpty(fromChartSelectors.createIsAutoOrManualUpdateChartSelector(FLAMINGO_RESERVED_REFS.Exploration))
          .pipe(
            filter((canUpdate) => canUpdate),
            map(() => {
              return {
                queryToken,
                chartType,
                chartToken,
                encoding,
              };
            })
          )
      ),
      switchMap(({ queryToken, chartType, chartToken, encoding }) =>
        this.dataViewFacade.createRunReadyByQueryToken(queryToken).pipe(
          filter((ready) => ready),
          switchMap(() =>
            combineLatest(
              rxStore.selectNoEmpty(ActiveExplorationSelectors.getGfQueryRunToken),
              actions$.pipe(
                ofType(SwitchAttrsActions.updateSwitchViewAttrSuccess),
                filter((a) => a.queryToken === queryToken),
                startWith(null)
              ),
              ((runToken) => runToken) as unknown as SchedulerLike // Drop the action out
            )
          ),
          withLatestFrom(this.dataViewFacade.model$, rxStore.select(ActiveExplorationSelectors.getFormat)),
          map(([runToken, model, chartFormat]) => {
            return {
              queryToken,
              model,
              runToken,
              chartFormat,
              chartToken,
              chartType,
              encoding,
            };
          })
        )
      )
    );

    // Chart - Exploration Designer selected visualization (execute plan)
    exploration$
      .pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            FLAMINGO_UNSUPPORTED_CHARTS.indexOf(chartType) < 0 &&
            chartType !== 'table' &&
            EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <= 0
        ),
        map(({ runToken, chartToken, model, queryToken, encoding, chartFormat, chartType }) => {
          const needsColorBackfill = isChart(chartType) && chartFormat.colors.map == null;
          const includeSortDomain = ChartFormatHelpers.useMuzeRenderer(chartType, chartFormat);
          return {
            runToken,
            chartToken,
            plan: QueryBuilder.buildPivotQuery(
              adapter,
              model,
              queryToken,
              encoding,
              needsColorBackfill,
              includeSortDomain
            ),
          };
        })
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: FLAMINGO_RESERVED_REFS.Exploration,
            queryRunToken: result.runToken as unknown as string,
            allowRefresh: true,
          })
        )
      );

    // Table - Exploration Designer selected visualization (execute plan)
    exploration$
      .pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            chartType === 'table' &&
            EncodingHelpers.validateTableEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
              0 &&
            EncodingHelpers.isChartEncodingComplete(encoding, chartType)
        ),
        withLatestFrom(rxStore.select(ChartDesignerSelectors.getExplorationTableOffset), (data, offset) => ({
          ...data,
          offset,
        })),
        map(({ queryToken, model, encoding, runToken, chartToken, offset, chartFormat }) => ({
          runToken,
          chartToken,
          plan: QueryBuilder.buildRecordsPlan(
            adapter,
            model,
            queryToken,
            encoding,
            chartFormat?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
            offset
          ),
        }))
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: FLAMINGO_RESERVED_REFS.Exploration,
            queryRunToken: result.runToken as unknown as string,
            allowRefresh: true,
          })
        )
      );

    /************************************
     *
     * EXPLORATION FLAMINGO EFFECTS - END
     *
     ***********************************/

    // Table - Explorer/Designer paging
    // Effect for GF context
    actions$
      .pipe(
        ofType(ChartDesignerActionType.SetPagingOffset, ChartDesignerActionType.SetPageSize),
        debounceTime(PAGING_DEBOUNCE_TIME),
        withLatestFrom(
          rxStore.select(fromChartSelectors.selectedToken),
          rxStore.select(ExplorerSelectors.getModalIsOpenState), // TODO: using this selector for the time being which doesn't account for the model
          rxStore.select(ExplorerSelectors.getChartStateGf)
        ),
        switchMap(([action, chartToken, explorerOpen, explorerChart]) => {
          // TODO: manually overriding temporarily. this effect will only work for the explorer until the selectedViztoken selector above is
          // refactored. After that, remove this!
          chartToken = FLAMINGO_RESERVED_REFS.ChartExplorer;
          return rxStore.selectNoEmpty(fromChartSelectors.createChartTypeEncodingSelector(chartToken)).pipe(
            filter(({ chartType }) => (explorerOpen ? explorerChart.chartType === 'table' : chartType === 'table')),
            first(),
            switchMap(
              ({ queryToken }) => this.dataViewFacade.createQueryRunTokenByQueryToken(queryToken),
              (data, runToken) => ({ ...data, runToken })
            ),
            withLatestFrom(
              this.dataViewFacade.model$,
              rxStore.select(fromChartSelectors.createChartFormatSelector(chartToken))
            ),
            map(([chart, model, chartFormat]) => {
              // Encoding used needs to match the explorer/designer environment, just as the refId below does below
              const encoding = explorerOpen ? explorerChart.encResult.encoding : chart.encoding;
              const pageSize = chartFormat?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE;
              let offset = 0;
              if (action.type === ChartDesignerActionType.SetPagingOffset) {
                offset = action.offset;
              }
              return {
                runToken: chart.runToken,
                chartToken,
                explorerOpen,
                plan: QueryBuilder.buildRecordsPlan(adapter, model, chart.queryToken, encoding, pageSize, offset),
              };
            })
          );
        })
      )
      .subscribe((result) => {
        const refId = result.explorerOpen ? FLAMINGO_RESERVED_REFS.ChartExplorer : FLAMINGO_RESERVED_REFS.ChartDesigner;
        rxStore.dispatch(
          FlamingoActions.executePagePlan({
            plan: result.plan,
            refId,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        );
      });

    // Explorer Chart Flow
    const explorerChart$ = this.createExplorerChart();

    // Table - Explorer visualization
    explorerChart$
      .pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            chartType === 'table' &&
            EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
            EncodingHelpers.validateTableEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <= 0
        ),
        withLatestFrom(this.rxStore.select(ExplorerSelectors.getSelectedChartFormat), (data, chartFormat) => ({
          ...data,
          chartFormat,
        })),
        switchMap(({ runToken, chartToken, queryToken, encoding, model, chartFormat }) =>
          rxStore.select(ChartDesignerSelectors.createPagingOffsetSelector(FLAMINGO_RESERVED_REFS.ChartExplorer)).pipe(
            first(),
            map((offset) => ({
              runToken,
              plan: QueryBuilder.buildRecordsPlan(
                adapter,
                model,
                queryToken,
                encoding,
                chartFormat?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
                offset
              ),
            }))
          )
        )
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: FLAMINGO_RESERVED_REFS.ChartExplorer,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        )
      );

    // Chart - Explorer visualization
    explorerChart$
      .pipe(
        filter(
          ({ chartType, model, queryToken, encoding }) =>
            FLAMINGO_UNSUPPORTED_CHARTS.indexOf(chartType) < 0 &&
            chartType !== 'table' &&
            EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <= 0
        ),
        withLatestFrom(this.rxStore.select(ExplorerSelectors.getSelectedChartFormat), (data, chartFormat) => ({
          ...data,
          chartFormat,
        })),
        map(({ runToken, model, queryToken, encoding, chartFormat, chartType }) => {
          const needsColorBackfill = isChart(chartType) && chartFormat.colors.map == null;
          const includeSortDomain = ChartFormatHelpers.useMuzeRenderer(chartType, chartFormat);
          return {
            runToken,
            plan: QueryBuilder.buildPivotQuery(
              adapter,
              model,
              queryToken,
              encoding,
              needsColorBackfill,
              includeSortDomain
            ),
          };
        })
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: FLAMINGO_RESERVED_REFS.ChartExplorer,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        )
      );

    actions$
      .pipe(
        ofType(reportEditActions.selectQuery, QueryActionType.BatchUpdateQueries),
        withLatestFrom(rxStore.select(transientSelectedQueryTokenSelector), (action, queryToken) => {
          const token = action.type === reportEditActions.selectQuery.type ? action.token || queryToken : queryToken;
          return typeof token === 'string' ? [token] : [];
        }),
        tap((queryTokenArray) => {
          if (queryTokenArray[0]) {
            // Init the paging, override if already set - We want to init both query and queryWithCalcs
            // plans for the cases where both exist and could have some paging values set. We need to force
            // back to the first page when rerunning queries as the results could be different and we could
            // be trying to view a page that doesn't exist.
            this.planMetadataFacade.initPaging(makeQueryPlanMetadataId(queryTokenArray[0]));
            this.planMetadataFacade.initPaging(makeQueryWithCalcsPlanMetadataId(queryTokenArray[0]));
          }
        }),
        watchMap((queryToken) =>
          this.dataViewFacade.createRunReadyByQueryToken(queryToken).pipe(
            filter((ready) => ready),
            switchMap(() =>
              combineLatest([
                this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(queryToken),
                // Block queries that have no rows
                this.dataViewFacade.getDatasetResultByQuery(queryToken),
              ]).pipe(
                filter(([runToken, datasetResult]) => datasetResult == null || datasetResult.rowCount > 0),
                map(([runToken]) => runToken),
                first()
              )
            ),
            withLatestFrom(
              this.dataViewFacade.createColumnsSwitchModelByQueryToken(queryToken),
              this.planMetadataFacade.createOffsetSelector(makeQueryPlanMetadataId(queryToken))
            ),
            // Add a filter to ensure model is defined before proceeding
            // Bug  --->  https://app.bugsnag.com/mode-analytics/webapp-frontend/errors/680a2571f0d0ce3f5da91479?filters%5Bevent.since%5D=all&filters%5Bapp.release_stage%5D=production&filters%5Bevent.severity%5D=error&filters%5Berror%5D=680a2571f0d0ce3f5da91479
            filter(([runToken, model, offset]) => !!model),
            map(([runToken, model, offset]) => ({
              runToken,
              queryToken,
              plan: RecordsPlan.make(
                adapter,
                model,
                queryToken,
                RecordsQuery.make({
                  limit: 100,
                  offset: offset,
                  fields: dimensions(adapter, model, queryToken),
                })
              ),
            }))
          )
        )
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: result.queryToken,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        )
      );

    // View Details SQL Results tables
    // This effect handles the GF case, there is no GF filter because it's only fired in the GF context
    // THe NON-GF context builds this effect in the report.view.data.service
    actions$
      .pipe(
        ofType(SwitchViewActions.queryRawDatasetAngularJs),
        switchMap((action) =>
          this.dataViewFacade.createRunReadyByQueryToken(action.queryToken).pipe(
            filter((ready) => ready),
            switchMap((_) =>
              this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(action.queryToken).pipe(first())
            ),
            withLatestFrom(
              this.dataViewFacade.createColumnsSwitchModelByQueryToken(action.queryToken),
              rxStore.select(SqlResultsSelectors.getSelectedQueryOffset)
            ),
            map(([runToken, model, offset]) => ({
              runToken,
              queryToken: action.queryToken,
              plan: RecordsPlan.make(
                adapter,
                model,
                action.queryToken,
                RecordsQuery.make({
                  limit: 100,
                  offset: offset,
                  fields: dimensions(adapter, model, action.queryToken),
                })
              ),
            }))
          )
        )
      )
      .subscribe((result) => {
        rxStore.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: result.queryToken,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        );
      });

    // SQL Results Paging (SQL results in editor and in view details)
    // GF effect
    actions$
      .pipe(
        ofType(SqlResultActions.Type.SetOffset),
        debounceTime(PAGING_DEBOUNCE_TIME),
        switchMap((action) =>
          this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(action.token).pipe(
            first(),
            withLatestFrom(this.dataViewFacade.createColumnsSwitchModelByQueryToken(action.token)),
            map(([runToken, model]) => ({
              runToken,
              queryToken: action.token,
              plan: RecordsPlan.make(
                adapter,
                model,
                action.token,
                RecordsQuery.make({
                  limit: 100,
                  offset: action.offset,
                  fields: dimensions(adapter, model, action.token),
                })
              ),
            }))
          )
        )
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePagePlan({
            plan: result.plan,
            refId: result.queryToken,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        )
      );

    // Table - Report paging
    // Effect for GF flow
    actions$
      .pipe(
        ofType(ChartActions.setOffset),
        debounceTime(PAGING_DEBOUNCE_TIME),
        switchMap((action) =>
          rxStore.selectNoEmpty(fromChartSelectors.createChartTypeEncodingSelector(action.token)).pipe(
            filter(({ chartType }) => chartType === 'table'),
            switchMap(
              ({ queryToken }) => this.dataViewFacade.createQueryRunTokenByQueryToken(queryToken),
              (data, runToken) => ({ ...data, runToken })
            ),
            withLatestFrom(
              this.dataViewFacade.model$,
              rxStore.select(reportFilterControlsSelector),
              rxStore.select(fromChartSelectors.createChartFormatSelector(action.token))
            ),
            map(([{ queryToken, encoding, chartToken, runToken }, model, filters, chartFormat]) => {
              // TODO: Consolidate filter application code
              const filteredEncoding = this.addValidReportFiltersToEncoding(filters, model, queryToken, encoding);
              return {
                runToken,
                chartToken,
                plan: QueryBuilder.buildRecordsPlan(
                  adapter,
                  model,
                  queryToken,
                  filteredEncoding,
                  chartFormat?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
                  action.offset
                ),
              };
            })
          )
        )
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePagePlan({
            plan: result.plan,
            refId: result.chartToken,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        )
      );

    // View Data
    // Effect for GF routes, currently dependent on other service methods. Can be moved to `flamingo-gf.effects` if those
    // are made more generic.
    actions$
      .pipe(
        ofType(ViewDataModalActions.openModal),
        switchMap((action) =>
          this.dataViewFacade.createRunReadyByQueryToken(action.queryToken).pipe(
            filter((ready) => ready),
            first(), // to avoid firing a view data plan if you open the modal window then run a different query
            switchMap(() =>
              this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(action.queryToken).pipe(first())
            ),
            withLatestFrom(this.dataViewFacade.model$),
            map(([runToken, model]) => ({
              queryToken: action.queryToken,
              runToken,
              model,
              encoding: action.encoding,
              seriesDetail: action.seriesDetail,
            }))
          )
        ),
        map(({ queryToken, runToken, model, encoding, seriesDetail }) => {
          return {
            runToken,
            plan: QueryBuilder.buildRecordsPlanWithDetail(adapter, model, queryToken, encoding, seriesDetail, 100, 0),
          };
        })
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: FLAMINGO_RESERVED_REFS.ViewData,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        )
      );

    // View Data - Paging
    // Effect for GF routes, currently dependent on other service methods. Can be moved to `flamingo-gf.effects` if those
    // are made more generic.
    actions$
      .pipe(
        ofType(ViewDataModalActions.setOffset),
        debounceTime(PAGING_DEBOUNCE_TIME),
        withLatestFrom(
          rxStore.select(ViewDataModalSelectors.selectQueryToken),
          rxStore.select(ViewDataModalSelectors.selectEncoding),
          rxStore.select(ViewDataModalSelectors.selectSeriesDetail)
        ),
        switchMap(([action, queryToken, encoding, seriesDetail]) =>
          this.dataViewFacade.createQueryRunTokenByQueryToken(queryToken).pipe(
            map((runToken) => ({
              queryToken,
              runToken,
              offset: action.offset,
              encoding,
              seriesDetail,
            }))
          )
        ),
        withLatestFrom(this.dataViewFacade.model$, rxStore.select(reportFilterControlsSelector)),
        map(([{ queryToken, encoding, seriesDetail, runToken, offset }, model]) => {
          return {
            runToken,
            plan: QueryBuilder.buildRecordsPlanWithDetail(
              adapter,
              model,
              queryToken,
              encoding,
              seriesDetail,
              100,
              offset
            ),
          };
        })
      )
      .subscribe((result) =>
        rxStore.dispatch(
          FlamingoActions.executePagePlan({
            plan: result.plan,
            refId: FLAMINGO_RESERVED_REFS.ViewData,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        )
      );

    // Refire the original execute plan action, but with allowRefresh false so it doesn't refresh again
    actions$
      .pipe(
        ofType(FlamingoActions.refreshSwitchViewSignatureSuccess),
        map((action) => ({ ...action.executePlanAction, allowRefresh: false }))
      )
      .subscribe((action) => rxStore.dispatch(action));
  }

  /**
   *
   * Create the explorer chart observable
   */
  private createExplorerChart() {
    return this.actions$.pipe(
      ofType(
        ChartActions.makeCopyTemp,
        ChartActions.updateChart,
        ChartActions.updateChartTemp,
        ChartActions.undoTemp,
        ChartActions.redoTemp,
        ChartActions.manuallyApplyChanges
      ),
      filter((action) => action.token === FLAMINGO_RESERVED_REFS.ChartExplorer),
      withLatestFrom(this.rxStore.selectNoEmpty(ExplorerSelectors.getChartForFlamingoPlan), (action, chart) => chart),
      filter(({ encoding, chartType }) => EncodingHelpers.isChartEncodingComplete(encoding, chartType)),
      switchMap(({ queryToken, chartType, token, encoding }) =>
        this.rxStore.selectNoEmpty(ExplorerSelectors.getIsAutoOrManualUpdate).pipe(
          filter((canUpdate) => canUpdate),
          map(() => {
            return {
              queryToken,
              chartType,
              token,
              encoding,
            };
          })
        )
      ),
      switchMap(({ queryToken, chartType, token, encoding }) =>
        this.dataViewFacade.createRunReadyByQueryToken(queryToken).pipe(
          filter((ready) => ready),
          switchMap(() => this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(queryToken).pipe(first())),
          withLatestFrom(this.dataViewFacade.model$),
          map(([runToken, model]) => ({
            queryToken,
            model,
            runToken,
            chartToken: token,
            chartType,
            encoding,
          }))
        )
      ),
      shareReplay(1)
    );
  }

  private addValidReportFiltersToEncoding(
    filters: LegacyReportFilterControl[],
    model: Model<DataRole>,
    queryToken: string,
    encoding: Encoding
  ): Encoding {
    return addReportFiltersToChartEncoding(
      Model.Views.find(model, queryToken),
      encoding,
      Flamingo.getValidReportFilters(model, queryToken, filters)
    );
  }
}
